import { ColumnDataType, Provider, WorkflowCodeType, WorkflowComponentOutputType, WorkflowComponentParamType, WorkflowComponentParameterMode } from '../types';
import { WorkflowComponent } from './common/workflowcomponent';
import dedent from 'ts-dedent';
import { v4 as uuid4 } from 'uuid';
class Unique extends WorkflowComponent {
    constructor() {
        super({
            name: 'alteryx.unique',
            group: 'Data Preparation',
            title: 'Unique',
            description: `This component separates unique rows from duplicated ones.`,
            tags: ['alteryx']
        }, [
            {
                name: 'source',
                type: WorkflowComponentParamType.InputTable,
                title: 'Main table'
            },
            {
                name: 'columns',
                type: WorkflowComponentParamType.Column,
                dataType: [
                    ColumnDataType.Boolean,
                    ColumnDataType.Geography,
                    ColumnDataType.Geometry,
                    ColumnDataType.Number,
                    ColumnDataType.String
                ],
                parent: 'source',
                title: 'Columns to find unique values',
                mode: WorkflowComponentParameterMode.Multiple
            }
        ], [
            {
                name: 'unique',
                title: 'Unique',
                description: 'Table with unique rows',
                type: WorkflowComponentOutputType.Table
            },
            {
                name: 'duplicated',
                title: 'Duplicated',
                description: 'Table with duplicated rows',
                type: WorkflowComponentOutputType.Table
            }
        ]);
        this.supportedProviders = [Provider.BigQuery];
    }
    async getCode({ workflow, params, connection, envVars, codeType, schemas }) {
        const outputTablenameUnique = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'unique', codeType);
        const outputTablenameDuplicated = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'duplicated', codeType);
        const storedProc = codeType === WorkflowCodeType.StoredProcedure;
        switch (connection.type) {
            case Provider.BigQuery:
                const preparedParams = this._prepareParameters(params);
                const columnsString = preparedParams.columns
                    .map((x) => `'${x}'`)
                    .join(', ');
                const selectUnique = `
            WITH __grouped AS
            (
              SELECT
                %s1, COUNT(*) AS __count
              FROM
                \`${preparedParams.source}\` AS a
              GROUP BY
                %s2
            )
            SELECT
              * EXCEPT(__count)
            FROM
              __grouped
            WHERE
              __count = 1`;
                const createTableUnique = connection.getCreateTempTableQuery(outputTablenameUnique, selectUnique, codeType, null);
                const selectDuplicated = `
            WITH __grouped AS
            (
              SELECT
                %s1, COUNT(*) AS __count
              FROM
                \`${preparedParams.source}\` AS a
              GROUP BY
                %s2
            )
            SELECT
              * EXCEPT(__count)
            FROM
              __grouped
            WHERE
              __count > 1`;
                const createTableDuplicated = connection.getCreateTempTableQuery(outputTablenameDuplicated, selectDuplicated, codeType, null);
                const tokens = preparedParams.source?.split('.');
                const datasetname = storedProc
                    ? connection.tempStoragePath
                    : `${tokens[0]}.${tokens[1]}`;
                const tablename = storedProc
                    ? `table_${uuid4().replace(/-/g, '_')}`
                    : tokens[2];
                const storedProcTable = connection.quoteName(`${connection.tempStoragePath}.${tablename}`);
                const query = dedent `
            DECLARE cols STRING;
            DECLARE colsGroupBy STRING;
            ${storedProc
                    ? dedent `
                    CREATE TABLE ${storedProcTable} AS
                    SELECT * FROM ${connection.quoteName(preparedParams.source)}
                    WHERE 1=0;`
                    : ''}
            EXECUTE IMMEDIATE
            '''
              WITH __cols AS(
                SELECT
                  FORMAT(
                    CASE data_type
                    WHEN 'GEOGRAPHY' THEN
                      'ST_GEOGFROMWKB(ANY_VALUE(ST_ASBINARY(%s))) AS %s'
                    ELSE
                      'ANY_VALUE(%s) AS %s'
                    END,
                    column_name,
                    column_name
                  ) cols
                FROM
                  \`${datasetname}\`.INFORMATION_SCHEMA.COLUMNS
                WHERE table_name = '${tablename}'
              )
              SELECT STRING_AGG(cols, ', ')
              FROM __cols
            '''
            INTO cols;
            EXECUTE IMMEDIATE
            '''
              WITH __cols AS(
                SELECT
                  FORMAT(
                    CASE data_type
                    WHEN 'GEOGRAPHY' THEN
                      'ST_ASBINARY(a.%s)'
                    ELSE
                      'a.%s'
                    END,
                    column_name
                  ) cols
                FROM
                  \`${datasetname}\`.INFORMATION_SCHEMA.COLUMNS
                WHERE table_name = '${tablename}'
                AND column_name IN UNNEST([${columnsString}])
              )
              SELECT STRING_AGG(cols, ', ')
              FROM __cols
            '''
            INTO colsGroupBy;
            ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
            ${dedent(connection.executeDynamicSQL({
                    sql: createTableUnique,
                    addQuotes: true,
                    replacements: [
                        ["'%s1'", 'cols'],
                        ["'%s2'", 'colsGroupBy']
                    ]
                }))}
            ${dedent(connection.executeDynamicSQL({
                    sql: createTableDuplicated,
                    addQuotes: true,
                    replacements: [
                        ["'%s1'", 'cols'],
                        ["'%s2'", 'colsGroupBy']
                    ]
                }))}`;
                return {
                    query,
                    outputs: {
                        unique: outputTablenameUnique,
                        duplicated: outputTablenameDuplicated
                    }
                };
            default:
                return null;
        }
    }
}
export { Unique };
